import {useTranslation} from "react-i18next";
import {IconButton, type IContextualMenuItem, Spinner, SpinnerSize, useWindow} from "@fluentui/react";
import {ICON_PROPS} from "app/utils/icons";
import {toggleFeature, useAdminStore} from "app/state/admin";
import {query} from "app/gql/client";
import {LoginGenerateTokenDocument} from "app/gql/graphqlSchema";
import {useCallback, useEffect, useState} from "react";
import {QRCode} from "react-qr-code";
import {noop} from "app/utils/types";
import {styled} from "styled-components";

const TokenWrapper = styled.div`
  padding: 0.5rem;
`;

const GenerateLoginToken = () => {
  const [loginToken, setLoginToken] = useState<string | null>(null);
  const window = useWindow();

  const generateLoginToken = useCallback(async () => {
    const result = await query(LoginGenerateTokenDocument, {
      returnUrl: window?.location.href.replace(window.location.origin, "") ?? "/"
    });
    setLoginToken(`${window?.location.origin}/i/teleport/${result.loginGenerateToken}`);
  }, [window?.location.href, window?.location.origin]);

  useEffect(() => {
    generateLoginToken().finally(noop);
  }, [generateLoginToken]);

  return (
    <TokenWrapper>
      {loginToken ? (
        <a href={loginToken} rel="noreferrer" target="_blank">
          <QRCode size={256} value={loginToken} />
        </a>
      ) : (
        <Spinner size={SpinnerSize.large} />
      )}
    </TokenWrapper>
  );
};

export const AdminMenu = () => {
  const {t} = useTranslation("admin");
  const features = useAdminStore((state) => state.features);

  const toggleFeatureMenu =
    (feature: keyof typeof features): IContextualMenuItem["onClick"] =>
    (ev) => {
      toggleFeature(feature);
      ev?.preventDefault();
    };

  const menuItems: IContextualMenuItem[] = [
    {
      key: "video-stats",
      text: t("nav.admin.features.video-stats"),
      canCheck: true,
      checked: features.showVideoStats,
      onClick: toggleFeatureMenu("showVideoStats")
    },
    {
      key: "allow-seeking",
      text: t("nav.admin.features.allow-seeking"),
      canCheck: true,
      checked: features.allowSeeking,
      onClick: toggleFeatureMenu("allowSeeking")
    },
    {
      key: "disable-chapters",
      text: t("nav.admin.features.disable-chapters"),
      canCheck: true,
      checked: features.disableChapters,
      onClick: toggleFeatureMenu("disableChapters")
    },
    {
      key: "highlight-answers",
      text: t("nav.admin.features.highlight-answers"),
      canCheck: true,
      checked: features.highlightAnswers,
      onClick: toggleFeatureMenu("highlightAnswers")
    },
    {
      key: "test-notifications",
      text: t("nav.admin.features.test-notifications"),
      canCheck: true,
      checked: features.testSendEnabled,
      onClick: toggleFeatureMenu("testSendEnabled")
    },
    {
      key: "control-card-extras",
      text: t("nav.admin.features.control-card-extras"),
      canCheck: true,
      checked: features.controlCardExtras,
      onClick: toggleFeatureMenu("controlCardExtras")
    },
    {
      key: "transfer-device",
      text: t("nav.admin.features.transfer-device"),
      subMenuProps: {
        items: [
          {
            key: "generate-link",
            onRender: () => <GenerateLoginToken />
          }
        ]
      }
    }
  ];

  return <IconButton iconProps={ICON_PROPS.gearFull} menuProps={{items: menuItems}} />;
};
